import React from 'react';
import { SearchDropdown } from '../../../../../components';
import { Structural } from '../../../../../types';

interface StructuralSelectorProps {
  onFramingSizeSelect: (value: string | undefined) => void;
  onFramingTypeSelect: (value: string | undefined) => void;
  onRafterSpacingSelect: (value: string | undefined) => void;
  onAssumedChange: (isAssumed: boolean) => void;
  selectedFramingSize?: string;
  selectedFramingType?: string;
  selectedRafterSpacing?: string;
  isAssumed?: boolean;
  isEnabled: boolean;
  framingSizes: Structural[];
  framingTypes: Structural[];
  rafterSpacings: Structural[];
  isLoading: boolean;
  error?: string;
}

export const StructuralSelector: React.FC<StructuralSelectorProps> = ({
  onFramingSizeSelect,
  onFramingTypeSelect,
  onRafterSpacingSelect,
  onAssumedChange,
  selectedFramingSize,
  selectedFramingType,
  selectedRafterSpacing,
  isAssumed,
  isEnabled,
  framingSizes,
  framingTypes,
  rafterSpacings,
  isLoading,
  error
}) => {

  const handleFramingSizeSelect = (option: any) => {
    if (!option) {
      onFramingSizeSelect(undefined);
      return;
    }
    const framingSize = framingSizes.find(f => f.id === option.id);
    if (framingSize) {
      onFramingSizeSelect(framingSize.value);
    }
  };

  const handleFramingTypeSelect = (option: any) => {
    if (!option) {
      onFramingTypeSelect(undefined);
      return;
    }
    const framingType = framingTypes.find(f => f.id === option.id);
    if (framingType) {
      onFramingTypeSelect(framingType.value);
    }
  };

  const handleRafterSpacingSelect = (option: any) => {
    if (!option) {
      onRafterSpacingSelect(undefined);
      return;
    }
    const rafterSpacing = rafterSpacings.find(r => r.id === option.id);
    if (rafterSpacing) {
      onRafterSpacingSelect(rafterSpacing.value);
    }
  };

  const handleAssumedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onAssumedChange(e.target.checked);
  };

  const framingSizeOptions = framingSizes.map(option => ({
    id: option.id,
    label: option.value
  }));

  const framingTypeOptions = framingTypes.map(option => ({
    id: option.id,
    label: option.value
  }));

  const rafterSpacingOptions = rafterSpacings.map(option => ({
    id: option.id,
    label: option.value
  }));

  if (!isEnabled) {
    return (
      <div className="h-full">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-200">
            <svg className="w-3.5 h-3.5 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4h6v6H4V4zm10 0h6v6h-6V4zM4 14h6v6H4v-6zm10 0h6v6h-6v-6z" />
            </svg>
            <span className="text-[10px] 3xl:text-xs uppercase tracking-[0.15em] font-bold text-slate-600">Structural Configuration</span>
          </div>
          <div className="text-center py-6">
            <p className="text-gray-400 text-xs 3xl:text-sm">Select a task to configure structural details</p>
          </div>
      </div>
    );
  }

  return (
    <div className="h-full">
        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-200">
          <svg className="w-3.5 h-3.5 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4h6v6H4V4zm10 0h6v6h-6V4zM4 14h6v6H4v-6zm10 0h6v6h-6v-6z" />
          </svg>
          <span className="text-[10px] 3xl:text-xs uppercase tracking-[0.15em] font-bold text-slate-600">Structural Configuration</span>
        </div>

        <div className="space-y-4">
          {/* Row 1: Framing Size and Type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs 3xl:text-sm font-semibold text-gray-700 mb-1">
                Framing Size
              </label>
              <SearchDropdown
                options={framingSizeOptions}
                onSelect={handleFramingSizeSelect}
                placeholder="Choose framing size..."
                isLoading={isLoading}
                error={error}
                className="text-sm"
                inputClassName="h-8 text-xs 3xl:text-sm"
                value={selectedFramingSize ? framingSizeOptions.find(opt => opt.label === selectedFramingSize) || null : null}
              />
            </div>
            
            <div>
              <label className="block text-xs 3xl:text-sm font-semibold text-gray-700 mb-1">
                Framing Type
              </label>
              <SearchDropdown
                options={framingTypeOptions}
                onSelect={handleFramingTypeSelect}
                placeholder="Choose framing type..."
                isLoading={isLoading}
                error={error}
                className="text-sm"
                inputClassName="h-8 text-xs 3xl:text-sm"
                value={selectedFramingType ? framingTypeOptions.find(opt => opt.label === selectedFramingType) || null : null}
              />
            </div>
          </div>

          {/* Row 2: Rafter Spacing and Assumed Toggle */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
            <div>
              <label className="block text-xs 3xl:text-sm font-semibold text-gray-700 mb-1">
                Rafter Spacing
              </label>
              <SearchDropdown
                options={rafterSpacingOptions}
                onSelect={handleRafterSpacingSelect}
                placeholder="Choose rafter spacing..."
                isLoading={isLoading}
                error={error}
                className="text-sm"
                inputClassName="h-8 text-xs 3xl:text-sm"
                value={selectedRafterSpacing ? rafterSpacingOptions.find(opt => opt.label === selectedRafterSpacing) || null : null}
              />
            </div>

            <div className="flex items-center h-8">
              <button
                type="button"
                role="switch"
                aria-checked={isAssumed || false}
                onClick={() => onAssumedChange(!isAssumed)}
                disabled={!isEnabled}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  isAssumed ? 'bg-rose-500' : 'bg-slate-200'
                } ${isEnabled ? '' : 'opacity-60 cursor-not-allowed'}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    isAssumed ? 'translate-x-4' : 'translate-x-1'
                  }`}
                />
              </button>
              <span className="ml-2 text-xs 3xl:text-sm font-semibold text-gray-700">
                Assumed - No Measurements
              </span>
            </div>
          </div>
        </div>
    </div>
  );
};

